"use client";

import dynamic from "next/dynamic";

import SkeletonPromotionBoxServerComponent from "@/app/[lang]/hotels/components/SkeletonComponents/SkeletonPromotionBoxComponent/SkeletonPromotionBoxServerComponent";
import type { Breadcrumbs } from "@/libs/client/goApi/hotel/hotelServiceInterfaces/getHotelSeoContent";
import { convertToKebabCase } from "@/utils/common/convertHelper/convertToKebabCase";
import { FeaturesTypePathEnum } from "@/utils/enums/FeaturePathGlobalEnum";
import { PaginationTypeEnum } from "@/utils/enums/paginationEnum";

import SkeletonHotelListSeoServerComponent from "../../components/SkeletonHotelListSeoServerComponent";

const FaqAccordionComponent = dynamic(
  () => import("@/app/[lang]/hotels/components/FaqAccordionComponent"),
  { ssr: false },
);

const HotelPromotionsBoxComponent = dynamic(
  () =>
    import(
      "@/app/[lang]/hotels/components/HotelPromotionsBoxComponent/HotelPromotionsBoxComponent"
    ),
  {
    ssr: false,
    loading: () => (
      <SkeletonPromotionBoxServerComponent
        classNames={{ base: "pl-4 pt-0 md:pl-0 md:pt-10" }}
      />
    ),
  },
);

const SeoContentComponent = dynamic(
  () => import("@/app/[lang]/hotels/components/SeoContentComponent"),
  { ssr: false },
);

const ServiceInLocationComponent = dynamic(
  () =>
    import(
      "@/components/global/ServiceInLocationComponent/ServiceInLocationComponent"
    ),
  { ssr: false },
);

const HotelSearchContentLayoutComponent = dynamic(
  () =>
    import(
      "@/app/[lang]/hotels/search/components/HotelSearchContentLayoutComponent/HotelSearchContentLayoutComponent"
    ),
  {
    ssr: false,
    loading: () => <SkeletonHotelListSeoServerComponent />,
  },
);

const HotelAreaLevelGroupComponent = ({
  areaSlug,
  areaName,
  citySlug,
  cityName,
  countrySlug,
  countryName,
  isDesktopServer,
  params,
  total,
  pageLevelLists,
  serviceLocationHeadingText,
}: {
  areaSlug?: string;
  areaName?: string;
  citySlug?: string;
  cityName?: string;
  countrySlug?: string;
  countryName?: string;
  isDesktopServer: boolean;
  params: { lang: string };
  total: number;
  pageLevelLists: Breadcrumbs;
  serviceLocationHeadingText: string;
}) => {
  return (
    <div>
      <div className="mt-6 md:mt-0">
        <HotelPromotionsBoxComponent seoSlug={areaSlug} />
      </div>
      <div className="mx-auto flex w-full flex-col justify-center md:max-w-7xl">
        <HotelSearchContentLayoutComponent
          classNames={{ base: "mt-10" }}
          countrySlug={countrySlug}
          citySlug={citySlug}
          areaSlug={areaSlug}
          isHiddenComponentWhenHotelNotFound
          paginationType={PaginationTypeEnum.PAGINATION}
          isHiddenShare
          isOnlyHotelAndFilter
          isFilterSeo
          isSortByContent
          isDisablePopuploading
          isDesktopServer={isDesktopServer}
        />
        {/* <LazyLoad height={500} rootMargin="300px 0px"> */}
        <div className="my-10 flex flex-col gap-12 px-4 md:gap-20 md:px-0">
          <SeoContentComponent
            lang={params.lang}
            type="area"
            keyword={`${areaName}, ${cityName}, ${countryName}`}
            total={total}
          />
          <FaqAccordionComponent
            faqLevelType="area"
            pageLevelLists={pageLevelLists}
            lang={params.lang}
          />
          <div id={convertToKebabCase("serviceLocationCity")} className="">
            <h2 className="mb-4 text-xl font-bold text-text-base md:text-xxl">
              {serviceLocationHeadingText}
            </h2>
            <ServiceInLocationComponent
              typeSelf={FeaturesTypePathEnum.HOTELS}
              lang={params.lang}
              linkPath={`${countrySlug}/${citySlug}`}
              location={cityName}
              overrideClass="px-4 xl:px-0"
              customLinkPath={`${countrySlug}/${citySlug}/${areaSlug}`}
              customLocation={areaName}
            />
          </div>
        </div>
        {/* </LazyLoad> */}
      </div>
    </div>
  );
};

export default HotelAreaLevelGroupComponent;
